import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardActions, CardContent, Chip, Grid, IconButton, Stack, Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { useSnackbar } from 'notistack';
import { getProductoFallbackImage } from '../../utils/productImage';

export const MAX_FOTOS_GALERIA = 4;
export const MAX_FOTO_MB = 10;
const MAX_FOTO_BYTES = MAX_FOTO_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const ALLOWED_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp']);

export type GaleriaFotoExistente = {
  id: number;
  thumbnailUrl: string;
  nombreArchivoOriginal?: string | null;
  esPrincipal?: boolean;
};

export type PendingGaleriaFoto = {
  tempId: string;
  file: File;
  previewUrl: string;
  nombre: string;
};

export type GaleriaFotosPersistOps = {
  deleteFoto: (fotoId: number) => Promise<void>;
  uploadFoto: (archivo: File, esPrincipal: boolean) => Promise<void>;
  setFotoPrincipal: (fotoId: number) => Promise<void>;
};

const isSupportedImageFile = (file: File): boolean => {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  if (!ALLOWED_IMAGE_EXTENSIONS.has(extension)) {
    return false;
  }
  return !file.type || ALLOWED_IMAGE_TYPES.has(file.type.toLowerCase());
};

export type GaleriaFotosState = {
  fotosExistentes: GaleriaFotoExistente[];
  fotosPendientes: PendingGaleriaFoto[];
  fotoPrincipalKey: string | null;
  fotoIdsEliminadas: number[];
  fotosExistentesActivas: GaleriaFotoExistente[];
  totalFotosConfiguradas: number;
  setFromServer: (fotos: GaleriaFotoExistente[]) => void;
  handleFotoPrincipalChange: (key: string) => void;
  handleFotoFilesSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removePendingFoto: (tempId: string) => void;
  removeExistingFoto: (fotoId: number) => void;
  hasPendingChanges: () => boolean;
  persist: (ops: GaleriaFotosPersistOps) => Promise<void>;
  revokePendientes: () => void;
};

export function useGaleriaFotos(initialFotos: GaleriaFotoExistente[] = []): GaleriaFotosState {
  const { enqueueSnackbar } = useSnackbar();
  const [fotosExistentes, setFotosExistentes] = useState<GaleriaFotoExistente[]>(initialFotos);
  const [fotosPendientes, setFotosPendientes] = useState<PendingGaleriaFoto[]>([]);
  const [fotoPrincipalKey, setFotoPrincipalKey] = useState<string | null>(() => {
    const principal = initialFotos.find((f) => f.esPrincipal) ?? initialFotos[0] ?? null;
    return principal ? `existing-${principal.id}` : null;
  });
  const [fotoIdsEliminadas, setFotoIdsEliminadas] = useState<number[]>([]);

  const setFromServer = useCallback((fotos: GaleriaFotoExistente[]) => {
    setFotosExistentes(fotos);
    setFotoIdsEliminadas([]);
    setFotosPendientes((prev) => {
      prev.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      return [];
    });
    const principal = fotos.find((f) => f.esPrincipal) ?? fotos[0] ?? null;
    setFotoPrincipalKey(principal ? `existing-${principal.id}` : null);
  }, []);

  const fotosExistentesActivas = useMemo(
    () => fotosExistentes.filter((foto) => !fotoIdsEliminadas.includes(foto.id)),
    [fotosExistentes, fotoIdsEliminadas],
  );
  const totalFotosConfiguradas = fotosExistentesActivas.length + fotosPendientes.length;

  const handleFotoPrincipalChange = useCallback((key: string) => {
    setFotoPrincipalKey(key);
  }, []);

  const handleFotoFilesSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const archivos = Array.from(event.target.files ?? []);
    if (!archivos.length) {
      return;
    }

    const archivosValidos = archivos.filter((file) => isSupportedImageFile(file) && file.size <= MAX_FOTO_BYTES);
    const invalidosFormato = archivos.filter((file) => !isSupportedImageFile(file)).length;
    const invalidosTamano = archivos.filter((file) => file.size > MAX_FOTO_BYTES).length;

    if (invalidosFormato > 0) {
      enqueueSnackbar('Se omitieron imágenes con formato no compatible. Use JPG, PNG o WEBP.', { variant: 'warning' });
    }
    if (invalidosTamano > 0) {
      enqueueSnackbar(`Se omitieron imágenes que superan ${MAX_FOTO_MB} MB por foto.`, { variant: 'warning' });
    }

    if (!archivosValidos.length) {
      event.target.value = '';
      return;
    }

    const disponibles = Math.max(0, MAX_FOTOS_GALERIA - totalFotosConfiguradas);
    if (disponibles <= 0) {
      enqueueSnackbar(`Se admite un máximo de ${MAX_FOTOS_GALERIA} fotos.`, { variant: 'warning' });
      event.target.value = '';
      return;
    }

    const seleccionados = archivosValidos.slice(0, disponibles).map((file) => ({
      tempId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      nombre: file.name,
    }));

    setFotosPendientes((prev) => {
      const next = [...prev, ...seleccionados];
      setFotoPrincipalKey((current) => {
        if (current) return current;
        if (fotosExistentesActivas.length) return current;
        return next[0] ? `pending-${next[0].tempId}` : null;
      });
      return next;
    });

    if (archivosValidos.length > disponibles) {
      enqueueSnackbar(`Solo se agregaron ${disponibles} fotos porque el máximo es ${MAX_FOTOS_GALERIA}.`, { variant: 'info' });
    }

    event.target.value = '';
  }, [enqueueSnackbar, fotosExistentesActivas.length, totalFotosConfiguradas]);

  const removePendingFoto = useCallback((tempId: string) => {
    setFotosPendientes((prev) => {
      const foto = prev.find((item) => item.tempId === tempId);
      if (foto) {
        URL.revokeObjectURL(foto.previewUrl);
      }
      const next = prev.filter((item) => item.tempId !== tempId);
      setFotoPrincipalKey((current) => {
        if (current !== `pending-${tempId}`) return current;
        const fallbackExisting = fotosExistentesActivas[0];
        return fallbackExisting
          ? `existing-${fallbackExisting.id}`
          : next[0]
            ? `pending-${next[0].tempId}`
            : null;
      });
      return next;
    });
  }, [fotosExistentesActivas]);

  const removeExistingFoto = useCallback((fotoId: number) => {
    setFotoIdsEliminadas((prev) => Array.from(new Set([...prev, fotoId])));
    setFotoPrincipalKey((current) => {
      if (current !== `existing-${fotoId}`) return current;
      const fallbackExisting = fotosExistentesActivas.find((foto) => foto.id !== fotoId);
      const fallbackPending = fotosPendientes[0];
      return fallbackExisting
        ? `existing-${fallbackExisting.id}`
        : fallbackPending
          ? `pending-${fallbackPending.tempId}`
          : null;
    });
  }, [fotosExistentesActivas, fotosPendientes]);

  const hasPendingChanges = useCallback(
    () => fotoIdsEliminadas.length > 0 || fotosPendientes.length > 0 || Boolean(fotoPrincipalKey),
    [fotoIdsEliminadas.length, fotosPendientes.length, fotoPrincipalKey],
  );

  const revokePendientes = useCallback(() => {
    setFotosPendientes((prev) => {
      prev.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      return [];
    });
  }, []);

  const persist = useCallback(async (ops: GaleriaFotosPersistOps) => {
    for (const fotoId of fotoIdsEliminadas) {
      await ops.deleteFoto(fotoId);
    }

    const fotoPendientePrincipal = fotoPrincipalKey?.startsWith('pending-')
      ? fotoPrincipalKey.replace('pending-', '')
      : null;

    const pendientesOrdenadas = [...fotosPendientes].sort((a, b) => {
      if (a.tempId === fotoPendientePrincipal) return -1;
      if (b.tempId === fotoPendientePrincipal) return 1;
      return 0;
    });

    for (const foto of pendientesOrdenadas) {
      await ops.uploadFoto(foto.file, foto.tempId === fotoPendientePrincipal);
    }

    if (fotoPrincipalKey?.startsWith('existing-')) {
      const fotoId = Number(fotoPrincipalKey.replace('existing-', ''));
      if (!Number.isNaN(fotoId) && !fotoIdsEliminadas.includes(fotoId)) {
        await ops.setFotoPrincipal(fotoId);
      }
    }

    fotosPendientes.forEach((foto) => URL.revokeObjectURL(foto.previewUrl));
    setFotosPendientes([]);
    setFotoIdsEliminadas([]);
  }, [fotoIdsEliminadas, fotoPrincipalKey, fotosPendientes]);

  useEffect(() => () => {
    fotosPendientes.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al desmontar
  }, []);

  return {
    fotosExistentes,
    fotosPendientes,
    fotoPrincipalKey,
    fotoIdsEliminadas,
    fotosExistentesActivas,
    totalFotosConfiguradas,
    setFromServer,
    handleFotoPrincipalChange,
    handleFotoFilesSelected,
    removePendingFoto,
    removeExistingFoto,
    hasPendingChanges,
    persist,
    revokePendientes,
  };
}

type GaleriaFotosEditorProps = {
  title: string;
  subtitle?: string;
  emptyMessage?: string;
  inputId: string;
  state: GaleriaFotosState;
  disabled?: boolean;
};

const GaleriaFotosEditor: React.FC<GaleriaFotosEditorProps> = ({
  title,
  subtitle,
  emptyMessage = 'Todavía no hay fotos configuradas.',
  inputId,
  state,
  disabled = false,
}) => {
  const {
    fotosExistentesActivas,
    fotosPendientes,
    fotoPrincipalKey,
    totalFotosConfiguradas,
    handleFotoPrincipalChange,
    handleFotoFilesSelected,
    removePendingFoto,
    removeExistingFoto,
  } = state;

  const cupoRestanteFotos = Math.max(0, MAX_FOTOS_GALERIA - totalFotosConfiguradas);
  const contadorColor = totalFotosConfiguradas >= MAX_FOTOS_GALERIA
    ? 'error'
    : totalFotosConfiguradas >= MAX_FOTOS_GALERIA - 1
      ? 'warning'
      : 'primary';

  return (
    <Box sx={{ border: '2px dashed #d0d7e2', borderRadius: 3, p: 3, background: '#f8fafc' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle ?? `Principal + secundarias. Máximo ${MAX_FOTOS_GALERIA} fotos.`}
          </Typography>
        </Box>
        <Chip
          color={contadorColor}
          variant={totalFotosConfiguradas >= MAX_FOTOS_GALERIA ? 'filled' : 'outlined'}
          label={`${totalFotosConfiguradas} de ${MAX_FOTOS_GALERIA}`}
          sx={{ fontWeight: 700 }}
        />
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <label htmlFor={inputId}>
          <input
            id={inputId}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            style={{ display: 'none' }}
            onChange={handleFotoFilesSelected}
            disabled={disabled}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            component="span"
            disabled={disabled || totalFotosConfiguradas >= MAX_FOTOS_GALERIA}
            sx={{ fontWeight: 700, minWidth: 180 }}
          >
            Agregar fotos
          </Button>
        </label>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip size="small" label="Formatos: JPG, PNG, WEBP" />
        <Chip size="small" label={`Máx: ${MAX_FOTO_MB} MB`} />
        <Chip size="small" label="Ideal: 1:1 · 1200x1200 px+" />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Se generan versiones automáticas para web (miniatura y vista mediana).
      </Typography>

      <Grid container spacing={2}>
        {fotosExistentesActivas.map((foto) => (
          <Grid item xs={12} sm={6} md={3} key={`existing-${foto.id}`}>
            <Card
              variant="outlined"
              sx={{
                borderColor: fotoPrincipalKey === `existing-${foto.id}` ? 'primary.main' : 'divider',
                borderWidth: 1,
                boxShadow: fotoPrincipalKey === `existing-${foto.id}` ? '0 0 0 2px rgba(25,118,210,0.2)' : 'none',
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ position: 'relative', mb: 1 }}>
                  {fotoPrincipalKey === `existing-${foto.id}` && (
                    <Chip
                      size="small"
                      color="primary"
                      icon={<StarIcon sx={{ fontSize: 16 }} />}
                      label="Principal"
                      sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                    />
                  )}
                  <Box
                    component="img"
                    src={foto.thumbnailUrl}
                    alt={foto.nombreArchivoOriginal || `Foto ${foto.id}`}
                    sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 2, bgcolor: 'grey.100' }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.src = getProductoFallbackImage();
                    }}
                  />
                </Box>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  variant={fotoPrincipalKey === `existing-${foto.id}` ? 'contained' : 'outlined'}
                  startIcon={fotoPrincipalKey === `existing-${foto.id}` ? <StarIcon /> : <StarBorderIcon />}
                  onClick={() => handleFotoPrincipalChange(`existing-${foto.id}`)}
                  disabled={disabled}
                >
                  Principal
                </Button>
                <IconButton color="error" onClick={() => removeExistingFoto(foto.id)} disabled={disabled}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}

        {fotosPendientes.map((foto) => (
          <Grid item xs={12} sm={6} md={3} key={`pending-${foto.tempId}`}>
            <Card
              variant="outlined"
              sx={{
                borderColor: fotoPrincipalKey === `pending-${foto.tempId}` ? 'primary.main' : 'divider',
                borderWidth: 1,
                boxShadow: fotoPrincipalKey === `pending-${foto.tempId}` ? '0 0 0 2px rgba(25,118,210,0.2)' : 'none',
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ position: 'relative', mb: 1 }}>
                  {fotoPrincipalKey === `pending-${foto.tempId}` && (
                    <Chip
                      size="small"
                      color="primary"
                      icon={<StarIcon sx={{ fontSize: 16 }} />}
                      label="Principal"
                      sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                    />
                  )}
                  <Box
                    component="img"
                    src={foto.previewUrl}
                    alt={foto.nombre}
                    sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 2, bgcolor: 'grey.100' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Se sube al guardar
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  variant={fotoPrincipalKey === `pending-${foto.tempId}` ? 'contained' : 'outlined'}
                  startIcon={fotoPrincipalKey === `pending-${foto.tempId}` ? <StarIcon /> : <StarBorderIcon />}
                  onClick={() => handleFotoPrincipalChange(`pending-${foto.tempId}`)}
                  disabled={disabled}
                >
                  Principal
                </Button>
                <IconButton color="error" onClick={() => removePendingFoto(foto.tempId)} disabled={disabled}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {totalFotosConfiguradas === 0 && (
        <Box
          sx={{
            mt: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            backgroundColor: 'background.paper',
          }}
        >
          <PhotoLibraryOutlinedIcon color="disabled" sx={{ fontSize: 30, mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Te quedan {cupoRestanteFotos} de {MAX_FOTOS_GALERIA} disponibles.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GaleriaFotosEditor;

export type GaleriaFotosHandle = {
  persist: (ops: GaleriaFotosPersistOps) => Promise<void>;
  setFromServer: (fotos: GaleriaFotoExistente[]) => void;
  hasPendingChanges: () => boolean;
  revokePendientes: () => void;
};

type GaleriaFotosSlotProps = Omit<GaleriaFotosEditorProps, 'state'> & {
  initialFotos?: GaleriaFotoExistente[];
};

/** Slot con estado propio; útil para una galería por color vía refs. */
export const GaleriaFotosSlot = forwardRef<GaleriaFotosHandle, GaleriaFotosSlotProps>(
  ({ initialFotos, ...editorProps }, ref) => {
    const state = useGaleriaFotos(initialFotos);
    useImperativeHandle(ref, () => ({
      persist: state.persist,
      setFromServer: state.setFromServer,
      hasPendingChanges: state.hasPendingChanges,
      revokePendientes: state.revokePendientes,
    }), [state.persist, state.setFromServer, state.hasPendingChanges, state.revokePendientes]);

    return <GaleriaFotosEditor {...editorProps} state={state} />;
  },
);

GaleriaFotosSlot.displayName = 'GaleriaFotosSlot';
